import _ from 'underscore';
import Promise from 'bluebird';
import { InlineLocationRecommendation } from '@bingads-webui-campaign/inline-location-recommendation';
import { DeterministicWrapperView } from '@bingads-webui/deterministic-wrapper-view';
import { updateGridQuery } from '@bingads-webui-campaign/summary-grid-helper';
import { shouldSupportCashbackSettings } from '@bingads-webui-campaign/personalized-offer-shared';
import { withUiBlock } from '@bingads-webui/uiblocker';

import {
  TargetingScope,
  TargetingSettingsContext,
} from '@bingads-webui-campaign/targeting-settings-scaffolds';

import { pgridFactory } from '@bingads-webui-campaign/pgrid-factory';
import LocationsRepository from './locations-repository';
import LocationsGridColumnsConfig from './locations-grid-column-config';

const EMPTY_TEMPLATE = () => '';

let locationsEditorPromise = null;
function getLocationsEditor(getView) {
  if (locationsEditorPromise === null) {
    locationsEditorPromise = new Promise((resolve) => {
      getView().then((LocationTargetsView) => {
        TargetingSettingsContext.LocationTargets = LocationTargetsView.default;
          import(/* webpackMode: 'eager' */ './locations-editor/view/locations-editor')
            .then((result) => {
              resolve(result.LocationsEditor);
            });
      });
    });
  }
  return locationsEditorPromise;
}

export class LocationsGridView extends DeterministicWrapperView {
  viewFactory({
    router,
    scope,
    level,
    campaignId,
    adgroupId,
    dataSource,
    preferenceConfig,
    getGridConfig,
    getGridPlugins,
    filterService,
    setupDataSourceErrorHandling,
    renderRecommendation,
    getView,
  }) {
    super.setState({
      campaignId,
      adgroupId,
      dataSource,
      scope,
      level,
    });
    this.gridRemoved = false;
    this.filterService = filterService;
    this.router = router;
    this.renderRecommendation = renderRecommendation;
    this.getView = getView;
    const columnConfig = new LocationsGridColumnsConfig({
      scope,
      level,
      parentView: this,
    });

    this.repository = _.isUndefined(dataSource)
      ? new LocationsRepository({
        deps: {
          scope,
          level,
          afterFindAllCallback: _.bind(this.afterFindAllCallback, this),
        },
      }) : new dataSource.LocationsRepository({ deps: { scope, level } });

    const gridConfig = getGridConfig(this.repository, columnConfig);

    let initialGrid = true;
    this.grid = pgridFactory({
      gridConfig,
      plugins: getGridPlugins(this, columnConfig),
      preferenceConfig,
    });
    TargetingSettingsContext.logger.logMessage(
      'ClientTrace',
      { name: 'Info', value: 0 },
      'LocationsGridView.viewFactory',
      'Locations grid created.'
    );

    setupDataSourceErrorHandling(this, this.repository);
    filterService.on('filter-state-changed', () => {
      if (!this.gridRemoved) {
        updateGridQuery(
          this.grid.gridView,
          this.filterService.getCombinedFilter(), true
        );
      }
    });

    this.grid.gridView.on('willUpdate', (changes) => {
      // skip the preference write for sortable header when initial the grid
      // as this value is already read from preference.
      if (initialGrid) {
        initialGrid = false;
        return;
      }
      TargetingSettingsContext.handleSortChange(changes, this.grid.gridView, gridConfig.name);
    });
    this.checkGrid('BaseGridView.buildGrid');

    this.grid.gridView.on('didEdit', this.onItemModified, this);
    this.grid.gridView.on('editInherited', this.inlineEditMultiTargets, this);
    this.on('inlineEdit', this.inlineEdit, this);
    TargetingSettingsContext.eventDispatcher.on('targeting-settings:bulkRemoved', this.onBulkRemoved, this);

    this.showTooMuchDataToDownloadError = false;

    this.grid.gridView.once('didReload', () => {
      this.grid.gridView.once('didRedraw', () => { this.renderRecommendation(); });
    });

    return this.grid.gridView;
  }

  get template() {
    return EMPTY_TEMPLATE;
  }

  onFilterUpdated() {
    if (this.grid.filterState.getFilterBar() && this.$('.filter-bar').children().length === 0) {
      this.$('.filter-bar').append(this.grid.filterState.getFilterBar().$el);
    }
  }

  isSmartShoppingCampaign() {
    const campaign = TargetingSettingsContext.pageContext.CurrentCampaign;
    const campaignSubType = _.result(campaign, 'CampaignSubType');
    return campaignSubType === 12 || campaignSubType === 'ShoppingSmartAds';
  }

  addLocations(options) {
    let opts = _.defaults({}, {
      showBidMode: false,
      campaign: TargetingSettingsContext.pageContext.CurrentCampaign,
    });
    if (_.isObject(options)) {
      opts = _.defaults({}, opts, options);
    }
    this.showLocationEditor(opts);
  }

  inlineEdit(item) {
    const adGroup = _.has(item, 'Campaign') ? _.extend({ Campaign: item.Campaign }, item.AdGroup) : undefined;
    this.showLocationEditor({
      campaignId: item.Campaign ? item.Campaign.Id : this.model.get('campaignId'),
      adgroupId: item.AdGroup.Id,
      criterions: [item],
      showBidMode: true,
      adGroup,
      campaign: _.result(item, 'Campaign'),
    });
  }

  inlineEditMultiTargets(items) {
    if (items.length === 0) {
      return;
    }

    const item = items[0];
    const adGroup = _.has(item, 'Campaign') ? _.extend({ Campaign: item.Campaign }, item.AdGroup) : undefined;
    this.showLocationEditor({
      campaignId: item.Campaign ? item.Campaign.Id : this.model.get('campaignId'),
      adgroupId: item.AdGroup.Id,
      criterions: items.reverse(),
      showBidMode: true,
      adGroup,
      campaign: _.result(item, 'Campaign'),
    });
  }

  renderInlineLocationRecommendation() {
    const hideToolbar = () => {
      this.$('.grid-toolbar').hide();
      this.grid.toolbar.gridToolbarGroup.hideAllToolbars();
    };
    const showToolbar = () => {
      this.$('.grid-toolbar').show();
      this.grid.toolbar.gridToolbarGroup.showAllToolbars();
      this.grid.gridView.off('didRedraw', hideToolbar);
    };
    this.inlineLocationRecommendation = new InlineLocationRecommendation({
      scope: {
        customerId: TargetingSettingsContext.state.get('customer_id'),
        accountId: TargetingSettingsContext.state.get('account_id'),
        campaignId: this.model.get('campaignId'),
        adgroupId: this.model.get('adgroupId'),
      },
      isCampaignLevelLocation: this.model.get('level') === 0,
      deps: TargetingSettingsContext,
      mapHeight: '200px',
    }).render();
    this.inlineLocationRecommendation.on('add:locations', () => this.grid.gridView.refresh(true));
    this.inlineLocationRecommendation.on('expand:collapse', () => {
      hideToolbar();
      this.grid.gridView.on('didRedraw', hideToolbar);
    });
    this.inlineLocationRecommendation.on('close:collapse', () => {
      showToolbar();
    });
    this.inlineLocationRecommendation.on('update:recommendations', (recommendations) => {
      if (_.size(recommendations) === 0) {
        showToolbar();
      }
    });
    this.$('.inline-location-recommendation-container').append(this.inlineLocationRecommendation.el);
    this.on('remove:excludedlocations', () => this.inlineLocationRecommendation.trigger('refresh'));
    this.on('locationsUpdated', () => this.inlineLocationRecommendation.trigger('refresh'));
    this.on('open:locationEditor', () => this.$('.inline-location-recommendation-container').hide());
    const refreshInlineRecommendation = () => {
      if (this.$('.editor-container .deterministic-wrapper-view').length > 0) {
        _.delay(() => refreshInlineRecommendation(), 100);
        return;
      }
      this.inlineLocationRecommendation.refreshMap();
      this.inlineLocationRecommendation.trigger('refresh');
    };
    this.on('close:locationEditor', () => {
      this.$('.inline-location-recommendation-container').show();
      refreshInlineRecommendation();
    });
  }

  renderTrunk() {
    this.filterService.refreshSource();
    this.filterService.initializeCombinedFilterPromise.then(() => {
      if (!this.gridRemoved) {
        updateGridQuery(
          this.grid.gridView,
          this.filterService.getCombinedFilter(), true
        );
      }
    });
    super.renderTrunk();
    return this;
  }

  shouldSupportCashbackSettings(campaign = TargetingSettingsContext.pageContext) {
    return shouldSupportCashbackSettings(
      campaign,
      TargetingSettingsContext.pageContext.Permissions
    );
  }

  showLocationEditor({
    campaignId,
    adgroupId,
    criterions = [],
    showBidMode = true,
    adGroup,
    campaign,
    pagePerfMarker,
    perfMarker,
  }) {
    this.trigger('open:locationEditor');

    getLocationsEditor(this.getView).then((LocationsEditor) => {
      if (perfMarker) {
        perfMarker.willInit();
      }
      const supportCashbackSettings = this.shouldSupportCashbackSettings(
        campaign,
        TargetingSettingsContext.pageContext.Permissions
      );
      this.locationsEditor = new LocationsEditor({
        campaignId: _.isUndefined(campaignId) ? this.model.get('campaignId') : campaignId,
        adgroupId: _.isUndefined(adgroupId) ? this.model.get('adgroupId') : adgroupId,
        scope: this.model.get('scope'),
        level: this.model.get('level'),
        criterions,
        showBidMode,
        adGroup,
        isSmartShoppingCampaign: this.isSmartShoppingCampaign(),
        supportCashbackSettings,
        currentCampaign: campaign || TargetingSettingsContext.pageContext.CurrentCampaign,
        currentAdGroup: adGroup || TargetingSettingsContext.pageContext.CurrentAdGroup,
        pagePerfMarker,
      });
      this.locationsEditor.on('close', _.bind(this.onClose, this));
      this.showEditor(this.locationsEditor);
      if (perfMarker) {
        perfMarker.done();
      }
    });
  }

  onClose(updated) {
    this.locationsEditor = null;
    this.trigger('close:locationEditor');
    if (updated) {
      this.trigger('locationsUpdated');
    }
  }

  onBulkRemoved(gridView) {
    if (this.grid && this.grid.gridView !== gridView) {
      this.grid.gridView.refresh(true);
      if (this.inlineLocationRecommendation) {
        this.inlineLocationRecommendation.trigger('refresh');
      }
    }
  }

  remove() {
    TargetingSettingsContext.eventDispatcher.off(null, null, this);
    if (this.inlineLocationRecommendation) {
      this.inlineLocationRecommendation.remove();
    }
    this.gridRemoved = true;
    super.remove();
  }

  getOverThresholdMessage() {
    const scope = this.model.get('scope');
    const { i18n } = TargetingSettingsContext;
    return _.result({
      [TargetingScope.Account]: i18n.getString('TargetingSettings_Grid_Location_OverThreshold_Account'),
      [TargetingScope.Campaign]: i18n.getString('TargetingSettings_Grid_Location_OverThreshold_Campaign'),
    }, scope);
  }

  gridLoadErrorHandler(data) {
    if (_.any(data.errors, err => err.Code === 'TooMuchDataToDownload')) {
      this.showTooMuchDataToDownloadError = true;
    } else {
      window.App.views.alertCenter.showError(TargetingSettingsContext.i18n.getString('GenericErrorMessage'));
    }
  }

  afterFindAllCallback(options, data) {
    if (_.has(data, 'errors')) {
      this.gridLoadErrorHandler(data);
    } else {
      this.grid.gridView.deselectAll();
    }

    return data;
  }

  onItemModified(model, item, property) {
    if (model) {
      let updated = false;
      if (property === 'BidAdjustments') {
        updated = item.CriterionBid.Multiplier !== model.BidMultiplier;
        item.CriterionBid.Multiplier = model.BidMultiplier; // eslint-disable-line no-param-reassign
      }

      if (property === 'CashbackPercent') {
        updated = true;
      }

      if (updated) {
        const unblock = withUiBlock(this.$el);
        this.repository.update(item)
          .catch(() => {
            window.App.views.alertCenter.showError(TargetingSettingsContext.i18n.getString('GenericErrorMessage'));
          })
          .finally(() => {
            this.grid.gridView.refresh(true);
            unblock();
          });
      }
    }
  }

  showEditor(view) {
    // use defer here to wait for the dropdown menu to hide first
    _.defer(() => {
      if (this.editorView) {
        this.editorView.remove();
      }
      this.editorView = view;
      this.grid.gridView.overrideWith(this.editorView);

      this.editorView.once('close', (response) => {
        this.grid.gridView.overrideWith(null);
        this.editorView.remove();
        this.editorView = null;

        if (response) {
          this.grid.gridView.refresh(true);
        }
      });
      window.scrollTo(0, 0);
    });
  }

  checkGrid(api) {
    if (_.isUndefined(this.grid)) {
      TargetingSettingsContext.logger.logMessage(
        'ClientTrace',
        { name: 'Info', value: 0 },
        api,
        'Grid has not been created.'
      );
    }
  }

  get className() {
    return 'locations-view';
  }
}
